<template>
  <FileComponent
    ref="fileManager"
    upload-type="task-image"
    :max-file-size="maxFileSize"
    :mime-types="supportedMimeTypes"
    :show-button="true"
    button-class="sr-only"
    @upload-success="onUploadSuccess"
  >
    <form
      id="content-prompt-form"
      class="w-full max-w-lg py-4 relative"
      :class="
        disabled || loading || fileManager?.uploader?.isUploading.value
          ? 'form-valid'
          : 'form-invalid'
      "
      @submit.prevent="submitForm"
    >
      <div v-if="!isOnScheduleTab || (isOnScheduleTab && isSchedulingAllowed)">
        <!-- Media Preview Section -->
        <div
          v-if="
            formData?.images?.length ||
            formData?.videos?.length ||
            formData?.files?.length
          "
          class="bg-slate-100 dark:bg-surface-700 px-4 py-6 rounded-xl mb-2"
        >
          <div class="flex gap-x-2 flex-wrap">
            <!-- Video Previews -->
            <template v-for="(file, index) in formData.files" :key="`video-${index}`">
              <div
                v-if="file.type.includes('video')"
                role="button"
                class="group relative w-52"
              >
                <div
                  class="absolute scale-75 z-10 top-0 right-0 hidden group-hover:block"
                >
                  <AppButton
                    icon="pi pi-trash"
                    variant="danger"
                    type="button"
                    @click="videoToRemove = file.path"
                  />
                </div>
                <div
                  class="w-full aspect-video object-cover group-hover:brightness-75 duration-300 relative border dark:border-surface-700 rounded-md overflow-hidden cursor-pointer"
                  @click="previewVideoUrl = file.path"
                >
                  <video
                    :src="file.path"
                    class="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div class="absolute inset-0 flex items-center justify-center">
                    <i class="pi pi-play-circle text-4xl text-white opacity-70" />
                  </div>
                </div>
              </div>
            </template>

            <template v-for="(file, index) in formData.files" :key="`image-${index}`">
              <div
                v-if="file.type.includes('image')"
                role="button"
                class="group relative w-52"
              >
                <div
                  class="absolute scale-75 z-10 top-0 right-0 hidden group-hover:block"
                >
                  <AppButton
                    icon="pi pi-trash"
                    variant="danger"
                    type="button"
                    @click="imageToRemove = file.path"
                  />
                </div>
                <img
                  :src="file.path"
                  alt="Selected Image"
                  class="w-full aspect-square object-cover group-hover:brightness-75 duration-300 relative border dark:border-surface-700 rounded-md cursor-pointer"
                  @click="previewUrl = file.path"
                />
              </div>
            </template>
          </div>
        </div>

        <div
          v-if="fileManager?.uploader?.isUploading.value"
          class="flex items-center gap-x-2 font-medium text-slate-400 mb-2"
        >
          <SpinIcon v-if="!loading" class="w-3 h-3" />
          <div class="text-sm">
            {{
              currentUploadType === "task-video" ? "Uploading Video" : "Uploading Image"
            }}
          </div>
        </div>

        <div class="block relative">
          <p
            v-if="showRegenerateHelp"
            class="text-xs opacity-90 text-gray-400 mb-1 italic"
          >
            {{ $t("updatePrompt") }}
          </p>
          <CustomTextarea
            v-model="formData.content"
            :custom-class="`${maxTextAreaHeight} block`"
            :disabled="loading"
            :rows="5"
            :placeholder="$t('AddtextImage')"
            class="[&>.relative>.relative]:rounded-lg [&>.relative>.relative>textarea]:pb-10"
          />
          <div class="absolute bottom-4 left-2 flex items-center gap-2">
            <button
              class="border items-center gap-1.5 flex px-2 pr-2.5 py-1 rounded-md ma-text-secondary border-surface-300 dark:border-surface-600"
              type="button"
              :data-gtm-action="
                isQuickTask
                  ? analyticsEvents.QUICK_TASK.quick_task_close_image_picker
                  : analyticsEvents.TASKS.task_close_image_picker
              "
              @click="toggleImagePicker()"
            >
              <i class="pi pi-image text-sm" />
              <span class="text-sm">{{ $t("addFromLibrary") }}</span>
            </button>
            <div class="relative">
              <div
                v-if="showImagePicker"
                class="fixed bottom-16 sm:bottom-10 left-4 right-4 min-[28rem]:left-[unset] min-[28rem]:w-screen h-[calc(100vh-12rem)] md:right-auto max-w-md bg-surface-0 dark:bg-surface-800 border dark:border-surface-700 rounded-md z-10 shadow-2xl opacity-0 animate-[fade-in-up_0.2s_ease_forwards] overflow-hidden"
              >
                <ContentEditImageSection
                  class=""
                  :max-file-size="maxFileSize"
                  @image-selected="addImage"
                  @video-selected="addVideo"
                />
                <span class="absolute top-2 right-2 z-10">
                  <AppButton
                    variant="secondary"
                    icon="pi pi-times"
                    :data-gtm-action="
                      isQuickTask
                        ? analyticsEvents.QUICK_TASK.quick_task_close_image_picker
                        : analyticsEvents.TASKS.task_close_image_picker
                    "
                    @click="toggleImagePicker()"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        <p class="text-sm ma-text-secondary">
          {{ $t("taskAttachmentDragDrop") }}
          <span class="shrink-0">
            <HelpButton
              :label="$t('truncatedText.readMore')"
              help-key="tasks/attachment"
              class="py-0! px-2!"
            />
          </span>
        </p>
      </div>

      <template v-if="isOnScheduleTab">
        <transition
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-80"
          leave-from-class="opacity-100 max-h-80"
          leave-to-class="opacity-0 max-h-0"
        >
          <AppLoading v-if="isLoadingConfiguration" wrapper-class="h-80" />
          <AppNoContent
            v-else-if="!configuration"
            :description="$t('loadingError')"
            :button-text="$t('retry')"
            :loading="isRefetchingConfiguration"
            class="h-80"
            @button-action="refetchConfiguration"
          />
          <TaskScheduler
            v-else-if="isSchedulingAllowed"
            :schedule-time="formData.scheduleTime"
            :expanded="category === 'scheduled'"
            :disabled="loading"
            @remove-schedule="removeSchedule"
            @set-schedule="formData.scheduleTime = $event"
          />
          <FreeTrialScheduledTask
            v-else-if="!isSchedulingAllowed"
            class="min-h-[50vh]"
            :title="$t('newTask.upgradePlan')"
            :description="$t('freeTrialScheduledTaskInfo')"
            :button-text="$t('words.Upgrade')"
            :button-to="{ name: RouteNames.PLANS, params: {} }"
          />
        </transition>
      </template>

      <AppButton
        v-if="!isOnScheduleTab || (isOnScheduleTab && isSchedulingAllowed)"
        :disabled="disabled || loading || fileManager?.uploader?.isUploading.value"
        type="submit"
        :loading="loading"
        :label="generateButtonLabel"
        class="w-full mt-6"
        :data-gtm-action="
          isQuickTask
            ? analyticsEvents.QUICK_TASK.quick_task_submit_prompt
            : analyticsEvents.TASKS.submit_prompt
        "
      />

      <div
        v-if="fileManager?.isOverDropZone"
        class="bg-blue-50 bg-opacity-25 absolute w-full h-full border-4 border-dashed border-blue-50 top-0 left-0 -z-10 scale-105 p-20"
      ></div>
    </form>

    <GenericModal
      v-if="mediaToRemove"
      :title="mediaToRemove.title"
      :message="mediaToRemove.message"
      type="danger"
      @confirm="handleMediaRemoveConfirm"
      @cancel="handleMediaRemoveCancel"
    >
      <div>
        <img
          v-if="mediaToRemove.type === 'image'"
          :src="mediaToRemove.path"
          :alt="mediaToRemove.alt"
          class="h-20 w-20 -mt-6 rounded-md"
        />
        <video
          v-else
          :src="mediaToRemove.path"
          class="h-20 w-32 -mt-6 rounded-md object-cover"
          muted
          preload="metadata"
        />
      </div>
    </GenericModal>

    <ContentEditMediaPreview
      v-if="previewUrl || previewVideoUrl"
      :url="previewUrl || previewVideoUrl"
      :media-type="previewUrl ? 'image' : 'video'"
      full-screen
      @exit-full-screen="
        previewUrl = '';
        previewVideoUrl = '';
      "
    />
  </FileComponent>
</template>

<script setup lang="ts">
import type { ContentInput } from "@/types/formData";
import CustomTextarea from "./CustomTextarea.vue";
import SpinIcon from "../icons/SpinIcon.vue";
import FileComponent from "../upload/FileComponent.vue";
import { isPast } from "date-fns";
import TaskScheduler from "../scheduler/TaskScheduler.vue";
import { cloneDeep } from "lodash";
import { StorageSerializers } from "@vueuse/core";
import FreeTrialScheduledTask from "@/components/taskList/FreeTrialScheduledTask.vue";
import AppNoContent from "../shared/AppNoContent.vue";
import AppLoading from "../shared/AppLoading.vue";
import ContentEditImageSection from "../content/edit/ContentEditImageSection.vue";
import { useToast } from "vue-toast-notification";
import GenericModal from "../shared/GenericModal.vue";
import ContentEditMediaPreview from "../content/edit/attachments/ContentEditMediaPreview.vue";

const fileManager = ref<any>(null);
const { t } = withI18nHelpers();
const $toast = useToast(defaultToastOptions);
const [showImagePicker, toggleImagePicker] = useToggle();

const props = defineProps<{
  loading?: boolean;
  category: string;
  isQuickTask?: boolean;
}>();

const emit = defineEmits<{
  (e: "submit", data: ContentInput): void;
}>();

const maxFileSize = 195 * MEGABYTE;
const imageToRemove = ref("");
const videoToRemove = ref("");
const previewUrl = ref("");
const previewVideoUrl = ref("");
const currentUploadType = ref<"task-image" | "task-video">("task-image");

const imageMimeTypes = getFileMimeTypesForExtensions(["jpg", "png", "webp"]);
const videoMimeTypes = getFileMimeTypesForExtensions(["mp4", "webm", "mov"]);
const supportedMimeTypes = [...imageMimeTypes, ...videoMimeTypes];

const formData = reactive<ContentInput>({
  content: "",
  url: "",
  html: "",
  campaignId: "",
  images: [],
  videos: [],
  files: [],
});

const uploaded = ref<any>(null);

const {
  data: configuration,
  isLoading: isLoadingConfiguration,
  refetch: refetchConfiguration,
  isRefetching: isRefetchingConfiguration,
} = useAccountFeatureConfigurationRequest();

function onUploadSuccess(file: any) {
  uploaded.value = file;
  const contentType = file.state.data.contentType;
  const filePath = `${getEnv("VITE_FILES_DOMAIN")}/${file.state.data.path}`;

  if (contentType.includes("image")) {
    formData.images!.push(filePath);
    currentUploadType.value = "task-image";
  } else if (contentType.includes("video")) {
    if (!formData.videos) formData.videos = [];
    formData.videos.push(filePath);
    currentUploadType.value = "task-video";
  }

  formData.files!.push({
    type: contentType,
    path: filePath,
  });
}

function addImage(url: string) {
  if (!url) return;
  if (!formData.files) {
    formData.files = [];
  }
  const path = enforceFilesDomain(url);
  if (!path) return;
  if (formData.files.find(f => f.path === path)) {
    $toast.error("This file has already been added.");
    return;
  }
  formData.files.push({
    type: "image/png",
    path,
  });

  if (!formData.images) {
    formData.images = [];
  }
  formData.images.push(path);
  toggleImagePicker(false);
}

function addVideo(url: string) {
  if (!url) return;
  if (!formData.files) {
    formData.files = [];
  }
  const path = enforceFilesDomain(url);
  if (!path) return;
  if (formData.files.find(f => f.path === path)) {
    $toast.error("This file has already been added.");
    return;
  }
  formData.files.push({
    type: "video/mp4",
    path,
  });

  if (!formData.videos) {
    formData.videos = [];
  }
  formData.videos.push(path);
  toggleImagePicker(false);
}

function removeImage(image: string) {
  formData.files = formData.files?.filter(f => enforceFilesDomain(f.path) !== image);
  formData.images = formData.images?.filter(i => enforceFilesDomain(i) !== image);
  imageToRemove.value = "";
}

function removeVideo(video: string) {
  formData.files = formData.files?.filter(f => enforceFilesDomain(f.path) !== video);
  formData.videos = formData.videos?.filter(v => enforceFilesDomain(v) !== video);
  videoToRemove.value = "";
}

function handleMediaRemoveConfirm() {
  if (!mediaToRemove.value) return;
  if (mediaToRemove.value.type === "image") {
    removeImage(mediaToRemove.value.path);
  } else {
    removeVideo(mediaToRemove.value.path);
  }
}

function handleMediaRemoveCancel() {
  if (!mediaToRemove.value) return;
  if (mediaToRemove.value.type === "image") {
    imageToRemove.value = "";
  } else {
    videoToRemove.value = "";
  }
}

const isInvalidDate = computed(
  () =>
    formData.scheduleTime &&
    formData.scheduleTime.type === "date" &&
    isPast(new Date(`${formData?.scheduleTime?.value}.000Z`))
);

const hasChanges = computed(() => {
  const diff = objectDifference(formData, {});
  return Object.keys(diff.updates).length > 0;
});

const disabled = computed(() => {
  if (typeof formData.url === "string") {
    return (
      !hasChanges.value ||
      isInvalidDate.value ||
      (!formData.content?.trim() && !formData.html?.trim() && !isValidUrl(formData.url))
    );
  }
  return isInvalidDate.value;
});

function removeSchedule() {
  delete formData.scheduleTime;
}

const maxTextAreaHeight = computed(() => {
  return "max-h-[45vh]";
});

const mediaToRemove = computed(() => {
  if (imageToRemove.value) {
    return {
      path: imageToRemove.value,
      type: "image" as const,
      title: t("modal.removeImage.title"),
      message: t("modal.removeImage.message"),
      alt: t("modal.removeImage.alt"),
    };
  }
  if (videoToRemove.value) {
    return {
      path: videoToRemove.value,
      type: "video" as const,
      title: t("modal.removeVideo.title"),
      message: t("modal.removeVideo.message"),
      alt: t("modal.removeVideo.alt"),
    };
  }
  return null;
});

function submitForm() {
  if (typeof formData.content === "string") {
    formData.content = formData.content.trim();
  }

  if (!disabled.value) {
    if (!isOnScheduleTab.value) {
      removeSchedule();
    }
    emit("submit", Object.assign(formData, { html: formData.content }));
  }
}

const session = useSessionStorage<Record<string, any>>(
  "temporary_regenerate_task_key",
  null,
  { serializer: StorageSerializers.object }
);

const persist = ref();

onBeforeRouteUpdate((to, from) => {
  if (to.path === from.path) {
    persist.value = cloneDeep(formData);
  } else {
    persist.value = null;
  }
});

onDeactivated(() => {
  if (persist.value) {
    session.value = { input: cloneDeep(persist.value) };
  }
});

onActivated(() => {
  if (!session.value) return;
  const { input } = session.value;
  if (!input || typeof input !== "object") return;
  if (input.originalInput) {
    formData.content = input.originalInput;
  } else if (input.content) {
    formData.content = input.content;
  } else if (input.url) {
    formData.content = input.url;
  }
  if (input.images) {
    formData.images = input.images;
    input.images.forEach((path: string) => {
      if (!formData.files?.find(f => f.path === path)) {
        formData.files!.push({ type: "image/png", path });
      }
    });
  }
  if (input.videos) {
    formData.videos = input.videos;
    input.videos.forEach((path: string) => {
      if (!formData.files?.find(f => f.path === path)) {
        formData.files!.push({ type: "video/mp4", path });
      }
    });
  }
  session.value = null;
  showRegenerateHelp.value = true;
});

const isSchedulingAllowed = computed(() => {
  return !!configuration.value?.createScheduledTasks;
});

const isOnScheduleTab = computed(() => props.category === "scheduled");

const route = useRoute();
const createButtonLabel = computed(() => {
  const { category, type, key } = route.params;
  const name = `${category}/${type}/${key}`;

  const buttonTextMap: Record<string, string> = {
    "social-media/facebook/write-post": t("CreateFacebookContent"),
    "social-media/twitter/write-post": t("CreateTwitterContent"),
    "social-media/instagram/write-post": t("CreateInstagramPost"),
    "social-media/linkedin/write-post": t("CreateLinkedInPost"),
    "social-media/facebook/script-video": t("CreateFacebookVideoScript"),
    "communications-outreach/email-marketing/generate-single-message": t("ComposeEmail"),
    "communications-outreach/email-marketing/email-campaign-ideas": t("GenerateIdeas"),
  };

  return buttonTextMap[name] || t("CreateContent");
});

const showRegenerateHelp = ref(false);

const generateButtonLabel = computed(() => {
  if (props.loading && showRegenerateHelp.value) {
    return t("regeneratingContent");
  }
  return props.loading ? t("loading.wait") : createButtonLabel.value;
});

const handlePopulateEvent = () => {
  showRegenerateHelp.value = true;
};

onMounted(() => {
  document.addEventListener(POPULATE_EDITORJS_INPUT_EVENT, handlePopulateEvent);
});

onBeforeUnmount(() => {
  document.removeEventListener(POPULATE_EDITORJS_INPUT_EVENT, handlePopulateEvent);
});
</script>
